/*
 * Copyright (c) Microsoft. All rights reserved. Licensed under the MIT license. See full license in root of repo. -->
 *
 * This file shows how to use MSAL.js to get an access token to your server and pass it to the task pane.
 */

/* global console, localStorage, location, Office, window */

import { LogLevel, PublicClientApplication } from "@azure/msal-browser";
import { getUserData } from "./middle-tier-calls";
import { showMessage } from "./message-helper";
import jwt from "jsonwebtoken";
import { JwksClient } from "jwks-rsa";
import form from "form-urlencoded";
import * as https from "https";
import axios from "axios";

import { setStorage, getStorage, removeStorage } from "./utils";

import {DEFAULT_AZURE_FUNCTION_URL, 
  CMS_USER_LIST_CODE, 
  STORE_USER_INFO_CODE, 
  CLIENT_ID, CLIENT_SECRET,
  ALEX_ACCESS_TOKEN_CODE,
  REDIRECT_URL} from '../environment'

const DISCOVERY_KEYS_ENDPOINT = "https://login.microsoftonline.com/common/discovery/v2.0/keys";
const domain = "graph.microsoft.com";
const version = "v1.0";

const clientId = CLIENT_ID; // temporary

const accessScope = `api://${window.location.host}/${clientId}/access_as_user`;
const loginRequest = {
  scopes: [accessScope],
  extraScopesToConsent: ["user.read"],
};

const msalConfig = {
  auth: {
    clientId: clientId,
    authority: "https://login.microsoftonline.com/common",
    redirectUri: REDIRECT_URL, // Update config script to enable `https://${window.location.host}/fallbackauthdialog.html`,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage", // Needed to avoid "User login is required" error.
    storeAuthStateInCookie: true, // Recommended to avoid certain IE/Edge issues.
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

const publicClientApp = new PublicClientApplication(msalConfig);

let loginDialog = null;
let homeAccountId = null;
let callbackFunction = null;

Office.onReady(() => {
  if (Office.context.ui.messageParent) {
    publicClientApp
      .handleRedirectPromise()
      .then(handleResponse)
      .catch((error) => {
        Office.context.ui.messageParent(JSON.stringify({ status: "failure", result: error }));
      });

    // The very first time the add-in runs on a developer's computer, msal.js hasn't yet
    // stored login data in localStorage. So a direct call of acquireTokenRedirect
    // causes the error "User login is required". Once the user is logged in successfully
    // the first time, msal data in localStorage will prevent this error from ever hap-
    // pening again; but the error must be blocked here, so that the user can login
    // successfully the first time. To do that, call loginRedirect first instead of
    // acquireTokenRedirect.
    if (localStorage.getItem("loggedIn") === "yes") {
      publicClientApp.acquireTokenRedirect(loginRequest);
    } else {
      // This will login the user and then the (response.tokenType === "id_token")
      // path in authCallback below will run, which sets localStorage.loggedIn to "yes"
      // and then the dialog is redirected back to this script, so the
      // acquireTokenRedirect above runs.
      publicClientApp.loginRedirect(loginRequest);
    }
  }
}).catch(err => console.error(err));

function handleResponse(response) {
  if (response.tokenType === "id_token") {
    localStorage.setItem("loggedIn", "yes");
  } else {
    Office.context.ui.messageParent(
      JSON.stringify({ status: "success", result: response.accessToken, accountId: response.account.homeAccountId })
    );
  }
}

export async function dialogFallback(callback) {
  // Attempt to acquire token silently if user is already signed in.
  if (homeAccountId !== null) {
    const result = await publicClientApp.acquireTokenSilent(loginRequest);
    if (result !== null && result.accessToken !== null) {
      // const response = await getUserData(result.accessToken);
      // callbackFunction(response);
    }
  } else {
    callbackFunction = callback;

    // We fall back to Dialog API for any error.
    const url = "/fallbackauthdialog.html";
    showLoginPopup(url);
  }
}

// This handler responds to the success or failure message that the pop-up dialog receives from the identity provider
// and access token provider.
async function processMessage(arg) {
  // Uncomment to view message content in debugger, but don't deploy this way since it will expose the token.

  let messageFromDialog = JSON.parse(arg.message);

  if (messageFromDialog.status === "success") {
    // We now have a valid access token.
    loginDialog.close();

    // Configure MSAL to use the signed-in account as the active account for future requests.
    const homeAccount = publicClientApp.getAccountByHomeId(messageFromDialog.accountId);
    if (homeAccount) {
      homeAccountId = messageFromDialog.accountId; // Track the account id for future silent token requests.
      publicClientApp.setActiveAccount(homeAccount);
    }

    // handleValidateJwt(messageFromDialog.result)

    const BEARER_TOKEN = messageFromDialog.result;


    try {
      const tokenFinal = await axios
        .post(
          DEFAULT_AZURE_FUNCTION_URL+"/api/CMS_Get_Access_Token?code="+ALEX_ACCESS_TOKEN_CODE+"&assertion=" +
          BEARER_TOKEN,
          null,
          {
            headers: { Authorization: `Bearer ${BEARER_TOKEN}` }
          }
        )
        .then((data) => {
          return data;
        });

      if (tokenFinal.status == 200) {
        setStorage('bearerToken', BEARER_TOKEN);
        setStorage("tokenFinal", tokenFinal.data);
        axios
          .get("https://graph.microsoft.com/v1.0/me", {
            headers: {
              Authorization: "Bearer " + tokenFinal.data,
            },
          })
          .then((resp2) => {
            if (resp2.status == 200) {

              const userData = resp2.data;
              axios.get(DEFAULT_AZURE_FUNCTION_URL+'/api/CMS_User_List?code='+CMS_USER_LIST_CODE+'&user_email='+userData?.mail, {
                headers: { Authorization: `Bearer ${BEARER_TOKEN}` }
              }).then((resp3) => {
                if (resp3.data) {
                  const axiosData = resp3.data;

                  axios
                  .post(
                    DEFAULT_AZURE_FUNCTION_URL+"/api/CMS_Store_User_Info?code="+STORE_USER_INFO_CODE,
                    {
                      user_data: axiosData?.User_info,
                      persona: axiosData?.Persona,
                      custom_instruction: axiosData?.Custom_instruction,
                      is_pass_onboarding: axiosData?.is_pass_onboarding,
                      is_consent: axiosData?.is_consent
                    },
                    {
                      headers: { Authorization: `Bearer ${BEARER_TOKEN}` }
                    }
                  )
                  .then((resp3) => {
                    setStorage("currentLoggedInUser", resp3.data.user_information[0]);
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  });
  
                }
              }).catch((err) => {

                console.error(err)
                axios
                .post(
                  DEFAULT_AZURE_FUNCTION_URL+"/api/CMS_Store_User_Info?code="+STORE_USER_INFO_CODE,
                  {
                    user_data: resp2.data,
                    persona: "NIE",
                    custom_instruction: "",
                    is_pass_onboarding: false,
                    is_consent: false
                  },
                  {
                    headers: { Authorization: `Bearer ${BEARER_TOKEN}` }
                  }
                )
                .then((resp3) => {
                  setStorage("currentLoggedInUser", resp3.data.user_information[0]);
                  setTimeout(() => {
                    window.location.reload();
                  }, 1000);
                });

              })
              
            }
          })
          .catch((err) => {
            console.error(err)
          });
      } else {
        // 
      }
    } catch (error) {
      console.error(error)
    }

    // const response = await getUserData(messageFromDialog.result);
    // callbackFunction(response);
  } else if (messageFromDialog.error === undefined && messageFromDialog.result.errorCode === undefined) {
    // Need to pick the user to use to auth
  } else {
    // Something went wrong with authentication or the authorization of the web application.
    loginDialog.close();
    if (messageFromDialog.error) {
      showMessage(JSON.stringify(messageFromDialog.error.toString()));
    } else if (messageFromDialog.result) {
      showMessage(JSON.stringify(messageFromDialog.result.errorMessage.toString()));
    }
  }
}

function handleValidateJwt(bearerToken) {
  const token = bearerToken;
  const validationOptions = {
    audience: process.env.CLIENT_ID,
  };
}

// Use the Office dialog API to open a pop-up and display the sign-in page for the identity provider.
function showLoginPopup(url) {
  var fullUrl = location.protocol + "//" + location.hostname + (location.port ? ":" + location.port : "") + url;

  // height and width are percentages of the size of the parent Office application, e.g., PowerPoint, Excel, Word, etc.
  Office.context.ui.displayDialogAsync(fullUrl, { height: 60, width: 30 }, function (result) {

    try {
      loginDialog = result.value;
      loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processMessage);
      
    } catch (error) {
      console.error(error)
    }
  });
}
